import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  RefreshCw, ArrowRightLeft, TrendingUp, TrendingDown,
  DollarSign, Euro, PoundSterling, Coins, Globe, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import api from '../lib/api';

const CURRENCIES = [
  { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
  { code: 'USD', name: 'Dólar estadounidense', symbol: '$', flag: '🇺🇸' },
  { code: 'GBP', name: 'Libra esterlina', symbol: '£', flag: '🇬🇧' },
  { code: 'CHF', name: 'Franco suizo', symbol: 'CHF', flag: '🇨🇭' },
  { code: 'JPY', name: 'Yen japonés', symbol: '¥', flag: '🇯🇵' },
  { code: 'CAD', name: 'Dólar canadiense', symbol: 'C$', flag: '🇨🇦' },
  { code: 'AUD', name: 'Dólar australiano', symbol: 'A$', flag: '🇦🇺' },
  { code: 'MXN', name: 'Peso mexicano', symbol: '$', flag: '🇲🇽' },
  { code: 'BRL', name: 'Real brasileño', symbol: 'R$', flag: '🇧🇷' },
  { code: 'ARS', name: 'Peso argentino', symbol: '$', flag: '🇦🇷' },
  { code: 'CLP', name: 'Peso chileno', symbol: '$', flag: '🇨🇱' },
  { code: 'COP', name: 'Peso colombiano', symbol: '$', flag: '🇨🇴' },
  { code: 'CNY', name: 'Yuan chino', symbol: '¥', flag: '🇨🇳' },
  { code: 'INR', name: 'Rupia india', symbol: '₹', flag: '🇮🇳' },
  { code: 'KRW', name: 'Won surcoreano', symbol: '₩', flag: '🇰🇷' },
];

const MultiCurrency = () => {
  const { user, updateUser } = useAuth();
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Converter state
  const [fromCurrency, setFromCurrency] = useState('EUR');
  const [toCurrency, setToCurrency] = useState('USD');
  const [amount, setAmount] = useState('100');
  const [convertedAmount, setConvertedAmount] = useState(null);

  // Settings
  const [baseCurrency, setBaseCurrency] = useState(user?.currency || 'EUR');
  const [autoConvert, setAutoConvert] = useState(user?.auto_convert || false);
  const [showOriginal, setShowOriginal] = useState(user?.show_original_currency || true);

  useEffect(() => {
    loadRates();
  }, []);

  useEffect(() => {
    if (amount && rates[fromCurrency] && rates[toCurrency]) {
      const result = (parseFloat(amount) / rates[fromCurrency]) * rates[toCurrency];
      setConvertedAmount(result.toFixed(2));
    }
  }, [amount, fromCurrency, toCurrency, rates]);

  const loadRates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/currency/rates');
      setRates(response.data.rates);
      setLastUpdate(new Date(response.data.updated_at));
    } catch (error) {
      // Use fallback rates if API fails
      setRates({
        EUR: 1,
        USD: 1.08,
        GBP: 0.86,
        CHF: 0.95,
        JPY: 162.5,
        CAD: 1.47,
        AUD: 1.65,
        MXN: 18.5,
        BRL: 5.35,
        ARS: 890,
        CLP: 980,
        COP: 4200,
        CNY: 7.8,
        INR: 90,
        KRW: 1420,
      });
      setLastUpdate(new Date());
    } finally {
      setLoading(false);
    }
  };

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const saveSettings = async () => {
    try {
      await api.put('/user/currency-settings', {
        currency: baseCurrency,
        auto_convert: autoConvert,
        show_original_currency: showOriginal,
      });
      updateUser({ currency: baseCurrency, auto_convert: autoConvert, show_original_currency: showOriginal });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const getCurrencyIcon = (code) => {
    switch (code) {
      case 'EUR': return Euro;
      case 'USD': return DollarSign;
      case 'GBP': return PoundSterling;
      default: return Coins;
    }
  };

  const formatRate = (from, to) => {
    if (!rates[from] || !rates[to]) return '-';
    const rate = rates[to] / rates[from];
    return rate.toFixed(4);
  };

  return (
    <div className="space-y-6 pb-8" data-testid="multi-currency-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Globe className="text-primary" />
            Multi-Divisa
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Convierte y gestiona múltiples monedas
          </p>
        </div>
        <Button variant="outline" onClick={loadRates} disabled={loading}>
          <RefreshCw size={16} className={cn("mr-2", loading && "animate-spin")} />
          Actualizar tasas
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Converter */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowRightLeft className="text-primary" />
              Conversor de Divisas
            </CardTitle>
            <CardDescription>
              Conversión en tiempo real
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Cantidad</Label>
              <div className="relative">
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pr-20"
                  data-testid="amount-input"
                />
                <Select value={fromCurrency} onValueChange={setFromCurrency}>
                  <SelectTrigger className="absolute right-0 top-0 w-20 border-l rounded-l-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.flag} {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-center">
              <Button variant="ghost" size="sm" onClick={swapCurrencies}>
                <ArrowRightLeft size={16} />
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Resultado</Label>
              <div className="relative">
                <Input
                  type="text"
                  value={convertedAmount || ''}
                  readOnly
                  className="pr-20 bg-muted font-semibold text-lg"
                />
                <Select value={toCurrency} onValueChange={setToCurrency}>
                  <SelectTrigger className="absolute right-0 top-0 w-20 border-l rounded-l-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.flag} {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              1 {fromCurrency} = {formatRate(fromCurrency, toCurrency)} {toCurrency}
            </div>
          </CardContent>
        </Card>

        {/* Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="text-muted-foreground" />
              Configuración
            </CardTitle>
            <CardDescription>
              Preferencias de moneda
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Moneda principal</Label>
              <Select value={baseCurrency} onValueChange={setBaseCurrency}>
                <SelectTrigger data-testid="base-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.flag} {c.code} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Todos los totales se mostrarán en esta moneda
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Conversión automática</Label>
                <p className="text-xs text-muted-foreground">
                  Convertir transacciones a tu moneda principal
                </p>
              </div>
              <Switch 
                checked={autoConvert} 
                onCheckedChange={setAutoConvert}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Mostrar moneda original</Label>
                <p className="text-xs text-muted-foreground">
                  Ver también el importe en la moneda original
                </p>
              </div>
              <Switch 
                checked={showOriginal} 
                onCheckedChange={setShowOriginal}
              />
            </div>

            <Button onClick={saveSettings} className="w-full">
              Guardar cambios
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Exchange Rates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tasas de Cambio</CardTitle>
          <CardDescription>
            Referencia: {baseCurrency}
            {lastUpdate && (
              <span className="ml-2">
                • Actualizado: {lastUpdate.toLocaleString('es-ES')}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {CURRENCIES.filter(c => c.code !== baseCurrency).map((currency) => {
              const rate = formatRate(baseCurrency, currency.code);
              const rateNum = parseFloat(rate);
              
              return (
                <div 
                  key={currency.code}
                  className="p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{currency.flag}</span>
                    <span className="font-medium">{currency.code}</span>
                  </div>
                  <p className="text-lg font-semibold text-foreground">
                    {rate}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {currency.name}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground text-center">
            Las tasas de cambio se actualizan diariamente. Los valores son de referencia
            y pueden variar según tu entidad bancaria.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MultiCurrency;
